import { type FC, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import cx from 'clsx';

import CodeMirrorHTML from '@editor2/components/CodeMirrorHTML';
import Metadata from '@editor2/components/Metadata';
import { type SAQuestion } from '@editor2/types/pageElements';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { saQuestionElement } from './styles';
import ValidationsBlock from '../Question/ValidationsBlock';
import QuestionConfig from '../Question/QuestionConfig';

import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';
import { useDebounceProtectedState } from '@editor2/hooks/useDebounceProtectedState';
import { elementCommonStyles } from '@editor2/styles/element';

interface Props {
	element: SAQuestion;
	courseId: number;
	pageType: string;
}

const SaQuestionElement: FC<Props> = (props) => {
	const { element, courseId, pageType } = props;

	const commonPayload = { elementId: element.family_id, courseId };
	const debouncedDispatch = useDebouncedDispatch();

	// Mapper for hook consistency - simply extracts the fields we need to track locally
	const questionMapper = useCallback(
		({ body, reference_answer }: SAQuestion) => ({ body, reference_answer }),
		[]
	);

	const [questionState, handleLocalChange] = useDebounceProtectedState(element, questionMapper);

	const { body: questionBody, reference_answer: referenceAnswer } = questionState;

	const onDebouncedChange = (value) => {
		debouncedDispatch(updateElement({ ...commonPayload, ...value }));
	};

	const handleQuestionBodyChange = (value: string) => {
		handleLocalChange({ body: value, reference_answer: referenceAnswer });
		onDebouncedChange({ body: value });
	};

	const handleReferenceAnswerChange = (value: string) => {
		handleLocalChange({ body: questionBody, reference_answer: value });
		onDebouncedChange({ reference_answer: value });
	};

	return (
		<Form className={cx(saQuestionElement, elementCommonStyles)}>
			<Metadata
				courseId={courseId}
				metadata={element.metadata}
				onDebouncedChange={onDebouncedChange}
			/>

			<Form.Group>
				<CodeMirrorHTML
					value={questionBody}
					showTips={false}
					options={{ placeholder: 'Question', lineNumbers: false, gutters: [] }}
					onChange={(value) => handleQuestionBodyChange(value)}
				/>
			</Form.Group>

			<Form.Group className="answer">
				<div className="block" />
				<CodeMirrorHTML
					value={referenceAnswer}
					showTips={false}
					options={{ placeholder: 'Reference answer', lineNumbers: false, gutters: [] }}
					onChange={(value) => handleReferenceAnswerChange(value)}
				/>
			</Form.Group>

			<QuestionConfig element={element} courseId={courseId} pageType={pageType} />

			<ValidationsBlock element={element} courseId={courseId} />
		</Form>
	);
};

export default SaQuestionElement;
